import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowRight, Scale, Briefcase, Mountain, HardHat, TrendingUp, Users, Fuel, MessageCircle } from "lucide-react";
import PageShell from "@/components/PageShell";
import { SUBSIDIARIES, COMPANY } from "@/lib/content";
import { subsidiaryLogo } from "@/lib/assets";

const ICONS = { Scale, Briefcase, Mountain, HardHat, TrendingUp, Users, Fuel };

export default function CompanyDetail() {
  const { slug } = useParams();
  const sub = SUBSIDIARIES.find((s) => s.slug === slug);
  if (!sub) return <Navigate to="/companies" replace />;

  const Icon = ICONS[sub.icon] || Briefcase;
  const siblings = SUBSIDIARIES.filter((s) => s.slug !== sub.slug);
  const waHref = `https://wa.me/${(COMPANY.whatsapp || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent("Hello Salihi Group, I would like to enquire about " + sub.name + ".")}`;

  return (
    <PageShell
      testId={`company-detail-${sub.slug}`}
      title={sub.seo_title || `${sub.name} — Salihi Group`}
      description={sub.seo_description || sub.overview}
      eyebrow={sub.tagline}
      heading={sub.name}
      subheading={sub.description}
      breadcrumbs={[
        { label: "Our Companies", to: "/companies" },
        { label: sub.name },
      ]}
    >
      <div className="grid lg:grid-cols-12 gap-12">
        {/* Sidebar */}
        <aside className="lg:col-span-4 order-2 lg:order-1">
          <div className="bg-white border border-softgray p-8 sticky top-28">
            <div className="h-[110px] flex items-center justify-center mb-6 border-b border-softgray pb-6">
              <img
                src={subsidiaryLogo(sub)}
                alt={`${sub.name} logo`}
                className="max-h-full max-w-full object-contain"
                loading="lazy"
              />
            </div>
            <div className="flex items-center gap-3 mb-4">
              <Icon size={22} strokeWidth={1.5} className="text-gold" />
              <span className="text-gold text-[11px] uppercase tracking-[0.24em] font-medium">{sub.tagline}</span>
            </div>
            <h3 className="font-serif text-navy text-lg mb-6">A Salihi Group Company</h3>
            <Link
              to={`/contact?subject=${sub.slug}`}
              data-testid={`company-cta-sidebar-${sub.slug}`}
              className="btn-gold w-full justify-center"
            >
              Request Consultation <ArrowRight size={14} strokeWidth={2} />
            </Link>
            <div className="mt-6 pt-6 border-t border-softgray">
              <div className="text-mutedink text-xs uppercase tracking-wider mb-3">Reach us directly</div>
              <a href="tel:+252615243670" className="block text-navy hover:text-gold text-sm mb-1">+252 615 243 670</a>
              <a href="mailto:info@salihigroup.com" className="block text-navy hover:text-gold text-sm">info@salihigroup.com</a>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <div className="lg:col-span-8 order-1 lg:order-2">
          <section className="mb-12">
            <h2 className="font-serif text-navy text-2xl md:text-3xl font-light mb-6">Overview</h2>
            {Array.isArray(sub.overview) ? (
              <div className="space-y-4 text-navy text-[17px] leading-[1.8] font-light">
                {sub.overview.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            ) : (
              <p className="text-navy text-[17px] leading-[1.8] font-light">{sub.overview}</p>
            )}
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-navy text-2xl md:text-3xl font-light mb-6">What We Do</h2>
            <ul className="space-y-4">
              {sub.services.map((s, i) => (
                <li key={i} className="flex gap-4 items-start" data-testid={`company-service-${i}`}>
                  <span className="mt-1 shrink-0 w-6 h-6 border border-gold flex items-center justify-center">
                    <Check size={13} strokeWidth={2.5} className="text-gold" />
                  </span>
                  <span className="text-navy text-[16px] font-light leading-relaxed">{s}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mb-12 grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-serif text-navy text-xl font-light mb-4">Sectors we serve</h3>
              <ul className="space-y-2">
                {sub.sectors.map((sec) => (
                  <li key={sec} className="text-mutedink text-[15px] font-light">— {sec}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-serif text-navy text-xl font-light mb-4">Our strengths</h3>
              <ul className="space-y-2">
                {sub.strengths.map((s) => (
                  <li key={s} className="text-mutedink text-[15px] font-light">— {s}</li>
                ))}
              </ul>
            </div>
          </section>

          {sub.brandStatement && (
            <section className="mb-12">
              <div className="bg-navy text-white p-8 md:p-10 border-l-2 border-gold" data-testid={`brand-statement-${sub.slug}`}>
                <div className="text-gold text-[11px] uppercase tracking-[0.28em] font-medium mb-3">Our Brand</div>
                <blockquote className="font-serif text-white text-xl md:text-2xl font-light leading-snug italic">
                  &ldquo;{sub.brandStatement}&rdquo;
                </blockquote>
              </div>
            </section>
          )}

          {/* Sister companies */}
          <section className="pt-10 border-t border-softgray mb-12">
            <h3 className="font-serif text-navy text-xl font-light mb-6">Other Salihi Group companies</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {siblings.map((s) => (
                <Link
                  key={s.slug}
                  to={`/companies/${s.slug}`}
                  className="p-4 border border-softgray hover:border-gold transition-colors flex items-center gap-3"
                  data-testid={`related-company-${s.slug}`}
                >
                  <img src={subsidiaryLogo(s)} alt="" className="h-8 w-auto object-contain shrink-0" loading="lazy" />
                  <div className="min-w-0">
                    <div className="font-serif text-navy text-[13px] leading-tight truncate">{s.name}</div>
                    <div className="text-mutedink text-[10px] uppercase tracking-wider mt-1">{s.tagline}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* CTA band */}
          <section className="pt-10 border-t-2 border-gold">
            <h3 className="font-serif text-navy text-2xl md:text-3xl font-light mb-4">Enquire about {sub.name}.</h3>
            <p className="text-mutedink mb-6 max-w-2xl">
              Send us a message and a member of the Salihi Group team will be in touch shortly.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to={`/contact?subject=${sub.slug}`} className="btn-gold" data-testid={`company-cta-contact-${sub.slug}`}>
                Contact Us <ArrowRight size={14} strokeWidth={2} />
              </Link>
              <a href={waHref} target="_blank" rel="noreferrer" className="btn-outline-gold" data-testid={`company-cta-whatsapp-${sub.slug}`}>
                <MessageCircle size={14} strokeWidth={2} /> Chat on WhatsApp
              </a>
            </div>
          </section>
        </div>
      </div>
    </PageShell>
  );
}
